import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import PageHero from 'tio-ui/components/page-hero';
import {
  PhArrowLeft,
  PhArrowRight,
  PhChatCircle,
  PhChartDonut,
  PhCheckCircle,
  PhCode,
  PhCube,
  PhCursorClick,
  PhGridFour,
  PhLayout,
  PhListDashes,
  PhPalette,
  PhSidebar,
  PhStack,
  PhTag,
  PhTextbox,
  PhTrendUp,
  PhUserCircle,
  PhWarning,
} from 'ember-phosphor-icons';

<template>
  {{! template-lint-disable no-bare-strings }}
  <VStack class="w-full min-w-0">
    <div class="mb-4">
      <LinkTo @route="authenticated.reimagined" class="tio-demo-link">
        <PhArrowLeft @size={{18}} />
        Back to Reimagined
      </LinkTo>
    </div>

    <PageHero
      @title="Component Library"
      @subtitle="35 shared components in tio-reimagined. Battle-tested and ready for cross-app reuse."
      @icon={{PhCube}}
      @gradient="neutral"
      @decoration="dots"
    >
      <:actions>
        <Chip @intent="primary">
          <PhCode @size={{14}} class="mr-1" />
          35 Components
        </Chip>
        <Chip @intent="success">
          <PhCheckCircle @size={{14}} class="mr-1" />
          Fully Tested
        </Chip>
      </:actions>
    </PageHero>

    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      <LinkTo
        @route="authenticated.reimagined.components.select"
        class="tio-demo-catalog-card tio-demo-catalog-card--forms"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhListDashes @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Select</span>
            <p class="tio-demo-catalog-card__description">Single, multi, search, remote</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.drawer"
        class="tio-demo-catalog-card tio-demo-catalog-card--drawer"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhSidebar @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Drawer</span>
            <p class="tio-demo-catalog-card__description">Placement, sizes, portaled overlay</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.cards"
        class="tio-demo-catalog-card tio-demo-catalog-card--cards"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhGridFour @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Cards</span>
            <p class="tio-demo-catalog-card__description">Actionable, Highlight, Glass, Step</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.data-display"
        class="tio-demo-catalog-card tio-demo-catalog-card--data-display"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhTrendUp @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Data Display</span>
            <p class="tio-demo-catalog-card__description">StatCard, Progress, Pipeline, Timeline</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.navigation"
        class="tio-demo-catalog-card tio-demo-catalog-card--navigation"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhLayout @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Navigation</span>
            <p class="tio-demo-catalog-card__description">PageHero, NavCard, SectionHeader</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.feedback"
        class="tio-demo-catalog-card tio-demo-catalog-card--feedback"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhWarning @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Feedback</span>
            <p class="tio-demo-catalog-card__description">AlertBanner, EmptyState, IconBadge</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.patterns"
        class="tio-demo-catalog-card tio-demo-catalog-card--patterns"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhTag @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Patterns</span>
            <p class="tio-demo-catalog-card__description">FilterTabs, SkillChip, StatusBadge</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.forms"
        class="tio-demo-catalog-card tio-demo-catalog-card--forms"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhTextbox @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Forms</span>
            <p class="tio-demo-catalog-card__description">Input, Textarea, Checkbox, Radio, Switch,
              Form</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.buttons"
        class="tio-demo-catalog-card tio-demo-catalog-card--buttons"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhCursorClick @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Button</span>
            <p class="tio-demo-catalog-card__description">Intents, appearances, sizes, loading,
              icons</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.chips"
        class="tio-demo-catalog-card tio-demo-catalog-card--chips"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhTag @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Chip</span>
            <p class="tio-demo-catalog-card__description">Intents, sizes, icons, status badges</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.charts"
        class="tio-demo-catalog-card tio-demo-catalog-card--charts"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhChartDonut @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Charts</span>
            <p class="tio-demo-catalog-card__description">Donut, Bar, Line, Pie, Area, Summary</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.avatar"
        class="tio-demo-catalog-card tio-demo-catalog-card--data-display"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhUserCircle @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Avatar</span>
            <p class="tio-demo-catalog-card__description">Initials, images, colors, AvatarGroup</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.gradients"
        class="tio-demo-catalog-card tio-demo-catalog-card--gradients"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhPalette @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Gradients</span>
            <p class="tio-demo-catalog-card__description">Figma-synced branded backgrounds</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.overlays"
        class="tio-demo-catalog-card tio-demo-catalog-card--overlays"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhStack @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Overlays</span>
            <p class="tio-demo-catalog-card__description">Modal, ConfirmationDialog, Toast</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.popover"
        class="tio-demo-catalog-card tio-demo-catalog-card--popover"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhChatCircle @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">Popover</span>
            <p class="tio-demo-catalog-card__description">Space-aware, intents, hover mode, named
              blocks</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>

      <LinkTo
        @route="authenticated.reimagined.components.page-header"
        class="tio-demo-catalog-card tio-demo-catalog-card--page-header"
      >
        <div class="tio-row-top">
          <div class="tio-demo-catalog-card__icon">
            <PhLayout @size={{20}} />
          </div>
          <div>
            <span class="tio-demo-catalog-card__title">PageHeader</span>
            <p class="tio-demo-catalog-card__description">Two-zone header, PageControlBar, filters,
              tabs, count</p>
          </div>
        </div>
        <PhArrowRight @size={{16}} class="tio-demo-catalog-card__arrow" />
        <Chip @intent="primary" @size="sm" class="mt-3">New</Chip>
      </LinkTo>
    </div>

    <div class="tio-demo-catalog-summary">
      <div class="tio-row-top-loose">
        <div class="tio-demo-catalog-summary__icon">
          <PhCode @size={{24}} />
        </div>
        <div>
          <Heading @level={{3}} class="tio-fg-brand mb-2">Component Library Summary</Heading>
          <Text @variant="muted" class="mb-4">
            35 shared components in tio-reimagined, fully tested and ready for cross-app reuse in
            tio-employee, tio-admin, and tio-agent. Plus 4 overlay components (Modal,
            ConfirmationDialog, Toast, Popover).
          </Text>
          <Text @size="sm" @variant="muted" class="mb-2">tio-reimagined (35):</Text>
          <div class="tio-wrap-tight mb-3">
            <Chip @intent="success" @size="sm">ActionableCard</Chip>
            <Chip @intent="success" @size="sm">AlertBanner</Chip>
            <Chip @intent="primary" @size="sm">Avatar ✦</Chip>
            <Chip @intent="primary" @size="sm">AvatarGroup ✦</Chip>
            <Chip @intent="primary" @size="sm">Button ✦</Chip>
            <Chip @intent="primary" @size="sm">Chip ✦</Chip>
            <Chip @intent="success" @size="sm">CircularProgress</Chip>
            <Chip @intent="success" @size="sm">Drawer</Chip>
            <Chip @intent="success" @size="sm">EmptyState</Chip>
            <Chip @intent="success" @size="sm">FaqAccordion</Chip>
            <Chip @intent="success" @size="sm">FilterTabs</Chip>
            <Chip @intent="success" @size="sm">GlassCard</Chip>
            <Chip @intent="success" @size="sm">HighlightCard</Chip>
            <Chip @intent="success" @size="sm">IconBadge</Chip>
            <Chip @intent="success" @size="sm">InfoRow</Chip>
            <Chip @intent="success" @size="sm">MatchScoreBadge</Chip>
            <Chip @intent="success" @size="sm">NavCard</Chip>
            <Chip @intent="success" @size="sm">PageHero</Chip>
            <Chip @intent="success" @size="sm">PipelineFlow</Chip>
            <Chip @intent="success" @size="sm">PipelineStage</Chip>
            <Chip @intent="success" @size="sm">ProgressBar</Chip>
            <Chip @intent="success" @size="sm">SectionHeader</Chip>
            <Chip @intent="success" @size="sm">SectionPanel</Chip>
            <Chip @intent="primary" @size="sm">Select ✦</Chip>
            <Chip @intent="success" @size="sm">SkillChip</Chip>
            <Chip @intent="success" @size="sm">StatCard</Chip>
            <Chip @intent="success" @size="sm">StatusBadge</Chip>
            <Chip @intent="success" @size="sm">StepCard</Chip>
            <Chip @intent="success" @size="sm">TaEligibilityBadge</Chip>
            <Chip @intent="success" @size="sm">Timeline</Chip>
            <Chip @intent="success" @size="sm">TimelineTracker</Chip>
            <Chip @intent="primary" @size="sm">Modal ✦</Chip>
            <Chip @intent="primary" @size="sm">ConfirmationDialog ✦</Chip>
            <Chip @intent="primary" @size="sm">Toast ✦</Chip>
            <Chip @intent="primary" @size="sm">Popover ✦</Chip>
          </div>
        </div>
      </div>
    </div>
  </VStack>
</template>
